import React, { useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { makeStyles } from "@material-ui/core/styles"
import Dialog from "@material-ui/core/Dialog"
import MuiDialogTitle from "@material-ui/core/DialogTitle"
import HomeIcon from "@material-ui/icons/Home"
import BarChartIcon from "@material-ui/icons/BarChart"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import MoreHorizIcon from "@material-ui/icons/MoreHoriz"
import CollectionsIcon from "@material-ui/icons/Collections"
import FileCopyIcon from "@material-ui/icons/FileCopy"
import moment from "moment"
import { galleryList, replicateGalleryList } from "../v2_actions/gallery"
import { confirmModal } from "../v2_actions/ui"

export const MobileBottomNav = ({ activePage, galleryPickerOpen, setGalleryPickerOpen }) => {
    const classes = useStyles()
    const history = useHistory()
    const dispatch = useDispatch()
    const [showMoreMenu, setShowMoreMenu] = useState(false)
    const [showGalleryPicker, setShowGalleryPicker] = useState(false)
    const isPickerOpen = galleryPickerOpen || showGalleryPicker
    const closeGalleryPicker = () => { setShowGalleryPicker(false); if (setGalleryPickerOpen) setGalleryPickerOpen(false); }
    const galleries = useSelector(() => galleryList.getVisibleObjects())

    useEffect(() => {
        dispatch(galleryList.fetchListIfNeeded())
    }, [])

    const isActive = (page) => activePage === page

    return (
        <>
            <div className={classes.bottomNav}>
                <div className={classes.navItem} onClick={() => history.push("/photographer/map")}>
                    <HomeIcon className={isActive("home") ? classes.navIconActive : classes.navIcon} />
                    <span className={isActive("home") ? classes.navLabelActive : classes.navLabel}>Home</span>
                </div>
                <div className={classes.navItem} onClick={() => history.push("/photographer/mobile-sales")}>
                    <BarChartIcon className={isActive("sales") ? classes.navIconActive : classes.navIcon} />
                    <span className={isActive("sales") ? classes.navLabelActive : classes.navLabel}>Sales</span>
                </div>
                <div className={classes.navItem} onClick={() => history.push("/photographer/mobile-upload")}>
                    <CloudUploadIcon className={isActive("upload") ? classes.navIconActive : classes.navIcon} />
                    <span className={isActive("upload") ? classes.navLabelActive : classes.navLabel}>Upload</span>
                </div>
                <div className={classes.navItem} onClick={() => setShowMoreMenu(!showMoreMenu)}>
                    <MoreHorizIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>More</span>
                </div>
            </div>
            {showMoreMenu && (
                <div className={classes.moreMenu}>
                    <div className={classes.moreMenuItem} onClick={() => { setShowMoreMenu(false); history.push("/photographer/mobile-galleries"); }}>
                        <CollectionsIcon className={classes.moreMenuIcon} />
                        <span>Galleries</span>
                    </div>
                    <div className={classes.moreMenuItem} onClick={() => { setShowMoreMenu(false); setShowGalleryPicker(true); }}>
                        <FileCopyIcon className={classes.moreMenuIcon} />
                        <span>Copy Gallery</span>
                    </div>
                </div>
            )}
            <Dialog
                open={isPickerOpen}
                onClose={closeGalleryPicker}
                fullWidth
                maxWidth="sm"
                classes={{ paper: classes.pickerDialog }}
            >
                <MuiDialogTitle className={classes.pickerTitle}>
                    Select Gallery to Copy
                </MuiDialogTitle>
                <div className={classes.pickerList}>
                    {galleries.map((gallery) => (
                        <div
                            key={gallery.id}
                            className={classes.pickerItem}
                            onClick={() => {
                                closeGalleryPicker()
                                dispatch(confirmModal({
                                    title: "Copy Gallery",
                                    text: "Enter new gallery name",
                                    inputField: {
                                        defaultValue: `${gallery.session_name} copy`,
                                    },
                                    onConfirmed: (newSessionName) => {
                                        dispatch(replicateGalleryList.replicate({galleryRef: gallery.id, sessionName: newSessionName})).then(() => {
                                            dispatch(galleryList.invalidateList());
                                            dispatch(galleryList.fetchListIfNeeded());
                                            dispatch(confirmModal({
                                                title: "Gallery Copied",
                                                text: `Your gallery, ${newSessionName}, has been created. You can now add photos to this gallery using the "Upload Photos" function.`,
                                                onConfirmed: () => {},
                                                can_cancel: false,
                                            }));
                                        });
                                    },
                                    can_cancel: true,
                                }))
                            }}
                        >
                            <div className={classes.pickerItemName}>{gallery.session_name}</div>
                            <div className={classes.pickerItemMeta}>
                                {gallery.number_of_photos_in_gallery} photos — {moment.utc(gallery.shot_at_datetime).format("D MMM YYYY")}
                            </div>
                        </div>
                    ))}
                </div>
            </Dialog>
        </>
    )
}

const useStyles = makeStyles({
    bottomNav: {
        backgroundColor: "#fff",
        borderTop: "1px solid #e0e0e0",
        padding: "8px 0",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
    },
    navItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        padding: "4px 12px",
    },
    navIcon: {
        fontSize: "24px",
        color: "#0d2137",
        marginBottom: "2px",
    },
    navIconActive: {
        fontSize: "24px",
        color: "#1e88e5",
        marginBottom: "2px",
    },
    navLabel: {
        fontSize: "10px",
        color: "#0d2137",
    },
    navLabelActive: {
        fontSize: "10px",
        color: "#1e88e5",
        fontWeight: 600,
    },
    moreMenu: {
        position: "fixed",
        bottom: 56,
        right: 8,
        backgroundColor: "#fff",
        borderRadius: 8,
        boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        zIndex: 1001,
        overflow: "hidden",
    },
    moreMenuItem: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 24px",
        fontSize: 14,
        fontWeight: 500,
        color: "#0d2137",
        cursor: "pointer",
        "&:hover": {
            backgroundColor: "#f5f5f5",
        },
        borderBottom: "1px solid #eee",
        "&:last-child": {
            borderBottom: "none",
        },
    },
    moreMenuIcon: {
        fontSize: 20,
        color: "#1e88e5",
    },
    pickerDialog: {
        borderRadius: 12,
        maxHeight: "70vh",
    },
    pickerTitle: {
        background: "#214986",
        color: "#fff",
        fontSize: 14,
        fontWeight: 600,
        padding: "12px 20px",
    },
    pickerList: {
        overflowY: "auto",
        maxHeight: "55vh",
    },
    pickerItem: {
        padding: "14px 20px",
        borderBottom: "1px solid #eee",
        cursor: "pointer",
        "&:hover": {
            backgroundColor: "#f5f5f5",
        },
        "&:active": {
            backgroundColor: "#e8e8e8",
        },
    },
    pickerItemName: {
        fontSize: 14,
        fontWeight: 600,
        color: "#0d2137",
    },
    pickerItemMeta: {
        fontSize: 12,
        color: "#666",
        marginTop: 4,
    },
})
